// src/lib/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, signInWithPopup,
  GoogleAuthProvider, signOut, updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (uid) => {
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      if (snap.exists()) { setProfile(snap.data()); return snap.data(); }
    } catch (e) { console.error('fetchProfile error:', e); }
    return null;
  };

  const createProfile = async (firebaseUser, extras = {}) => {
    try {
      const ref = doc(db, 'users', firebaseUser.uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        // Referral attribution — captured from a ?ref=<uid> link, stashed in
        // localStorage so it survives the Google popup redirect too. Never
        // let someone accidentally attribute themselves (shouldn't be
        // possible since this only runs for a brand-new profile, but a
        // stale localStorage value from a previous session is a real edge
        // case worth guarding against).
        const referredBy = localStorage.getItem('gather-referral-code');
        const validReferral = referredBy && referredBy !== firebaseUser.uid ? referredBy : null;

        const data = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || '',
          photoURL: firebaseUser.photoURL || '',
          defaultTownId: 'loughrea',
          defaultCountyId: 'galway',
          referredBy: validReferral,
          createdAt: serverTimestamp(),
          ...extras,
        };
        await setDoc(ref, data);
        setProfile(data);
        localStorage.removeItem('gather-referral-code');
        return data;
      }
      return fetchProfile(firebaseUser.uid);
    } catch (e) {
      console.error('createProfile error:', e);
      return null;
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const p = await fetchProfile(firebaseUser.uid);
        if (!p) await createProfile(firebaseUser);
        // Get role from custom claims
        try {
          const tokenResult = await firebaseUser.getIdTokenResult();
          setProfile(prev => ({ ...prev, role: tokenResult.claims.role || 'user', claimTownId: tokenResult.claims.townId || null }));
        } catch {}
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const signUp = async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });
    await createProfile(cred.user, { displayName });
    return cred.user;
  };

  const signIn = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await fetchProfile(cred.user.uid);
    return cred.user;
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const cred = await signInWithPopup(auth, provider);
    await createProfile(cred.user);
    return cred.user;
  };

  const logOut = async () => {
    await signOut(auth);
    setUser(null);
    setProfile(null);
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.uid);
      try {
        const tokenResult = await user.getIdTokenResult(true);
        setProfile(prev => ({ ...prev, role: tokenResult.claims.role || 'user', claimTownId: tokenResult.claims.townId || null }));
      } catch {}
    }
  };

  const isOrganiser  = profile?.role === 'organiser' || profile?.role === 'admin';
  const isAdmin      = profile?.role === 'admin';
  const isTownAdmin  = profile?.role === 'town_admin';
  const townAdminScope = isTownAdmin ? profile?.claimTownId : null;
  // Has access to the /admin area at all — full admin OR scoped town admin.
  // Individual admin pages/sections still check isAdmin explicitly where
  // the action is financial or cross-town (Sponsors, Users, Analytics).
  const isAnyAdmin = isAdmin || isTownAdmin;

  return (
    <AuthContext.Provider value={{
      user, profile, loading, isOrganiser, isAdmin, isTownAdmin, townAdminScope, isAnyAdmin,
      signUp, signIn, signInWithGoogle, logOut, refreshProfile,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
